import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { EnvelopeIcon, ServerStackIcon, IdentificationIcon, KeyIcon } from '@heroicons/react/24/outline';
import api from '../api/axiosConfig';
import PageHeader from '../components/common/PageHeader';

const SmtpConfigForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [searchBusiness, setSearchBusiness] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const [newConfig, setNewConfig] = useState({
    businessId: '',
    host: '',
    port: 587,
    email: '',
    password: '',
    isActive: true
  });

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const res = await api.get('/v1/admin/businesses');
      if (res.data.success) {
        setBusinesses(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load businesses');
    }
  };

  const handleCreateConfig = async (e) => {
    e.preventDefault();
    if (!newConfig.businessId || !newConfig.host || !newConfig.port || !newConfig.email || !newConfig.password) {
      return toast.error('Please fill all required fields');
    }
    
    setLoading(true);
    try {
      const res = await api.post('/v1/admin/smtp-configs', newConfig);
      if (res.data.success) {
        toast.success(res.data.message);
        navigate('/smtp-configs');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create config');
    } finally {
      setLoading(false);
    }
  };

  const filteredBusinesses = businesses.filter(b => b.name.toLowerCase().includes(searchBusiness.toLowerCase()));

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto animate-fade-in">
      <PageHeader 
        title="Add SMTP Credential" 
        subtitle="Configure custom email settings for a business." 
        backLink="/smtp-configs"
      />

      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 overflow-hidden">
        <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center text-red-600">
              <EnvelopeIcon className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Connection Details</h2>
              <p className="text-gray-500 text-sm mt-1">Enter SMTP host and credentials securely</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleCreateConfig} className="p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Left Column */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Select Business</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <IdentificationIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type="text"
                    placeholder="Search or select business..."
                    value={searchBusiness}
                    onChange={(e) => {
                      setSearchBusiness(e.target.value);
                      setIsDropdownOpen(true);
                      if (e.target.value === '') setNewConfig({...newConfig, businessId: ''});
                    }}
                    onFocus={() => setIsDropdownOpen(true)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                  />
                  
                  {/* Custom Dropdown List */}
                  {isDropdownOpen && (
                    <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] max-h-48 overflow-y-auto">
                      <div 
                        className="py-2.5 px-4 hover:bg-purple-50 cursor-pointer text-sm text-gray-700 border-b border-gray-100 transition-colors bg-purple-50/30"
                        onClick={() => {
                          setNewConfig({...newConfig, businessId: 'system_default'});
                          setSearchBusiness(`System Default (Admin)`);
                          setIsDropdownOpen(false);
                        }}
                      >
                        <p className="font-bold text-purple-700">System Default (Admin)</p>
                        <p className="text-xs text-purple-500">Platform-wide fallback SMTP</p>
                      </div>
                      
                      {filteredBusinesses.length > 0 ? (
                        filteredBusinesses.map(b => (
                          <div 
                            key={b._id} 
                            className="py-2.5 px-4 hover:bg-red-50 cursor-pointer text-sm text-gray-700 border-b border-gray-50 last:border-0 transition-colors"
                            onClick={() => {
                              setNewConfig({...newConfig, businessId: b._id});
                              setSearchBusiness(`${b.name} (${b.ownerId?.email})`);
                              setIsDropdownOpen(false);
                            }}
                          >
                            <p className="font-bold">{b.name}</p>
                            <p className="text-xs text-gray-500">{b.ownerId?.email}</p>
                          </div>
                        ))
                      ) : (
                        <div className="py-3 px-4 text-sm text-gray-500 text-center">No businesses found</div>
                      )}
                    </div>
                  )}
                  {/* Invisible backdrop to close dropdown on click outside */}
                  {isDropdownOpen && (
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsDropdownOpen(false)}
                    ></div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type="email" 
                    required 
                    value={newConfig.email} 
                    onChange={(e) => setNewConfig({...newConfig, email: e.target.value})} 
                    placeholder="billing@company.com" 
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all" 
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">SMTP Host</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <ServerStackIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input 
                      type="text" 
                      required 
                      list="smtp-hosts"
                      value={newConfig.host} 
                      onChange={(e) => {
                        const val = e.target.value;
                        let newPort = newConfig.port;
                        // Auto-set common ports if known host is selected
                        if (val === 'smtp.gmail.com' || val === 'smtp.mail.yahoo.com' || val === 'smtp-mail.outlook.com') {
                          newPort = 587;
                        }
                        setNewConfig({...newConfig, host: val, port: newPort});
                      }} 
                      placeholder="e.g. smtp.gmail.com" 
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all" 
                    />
                    <datalist id="smtp-hosts">
                      <option value="smtp.gmail.com">Google / Gmail</option>
                      <option value="smtp-mail.outlook.com">Microsoft Outlook / Office 365</option>
                      <option value="smtp.mail.yahoo.com">Yahoo Mail</option>
                      <option value="smtp.zoho.in">Zoho Mail (IN)</option>
                      <option value="smtp.zoho.com">Zoho Mail (US)</option>
                      <option value="smtp.sendgrid.net">SendGrid</option>
                      <option value="smtp.mailgun.org">Mailgun</option>
                      <option value="email-smtp.us-east-1.amazonaws.com">Amazon SES</option>
                    </datalist>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Port</label>
                  <input 
                    type="number" 
                    required 
                    value={newConfig.port} 
                    onChange={(e) => setNewConfig({...newConfig, port: Number(e.target.value)})} 
                    placeholder="587" 
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">App Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input 
                    type="password" 
                    required 
                    value={newConfig.password} 
                    onChange={(e) => setNewConfig({...newConfig, password: e.target.value})} 
                    placeholder="Enter App Password securely" 
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all" 
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500">Your password is encrypted using AES-256-CBC.</p>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-100 flex items-center justify-end gap-4">
            <button 
              type="button" 
              onClick={() => navigate('/smtp-configs')} 
              className="px-6 py-3 text-sm font-bold text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="px-8 py-3 text-sm font-bold text-white bg-red-600 hover:bg-red-700 shadow-[0_4px_14px_0_rgba(220,38,38,0.39)] rounded-xl transition-all hover:-translate-y-0.5 disabled:opacity-70 disabled:hover:translate-y-0 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : 'Save Credential'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SmtpConfigForm;
